/**
 * pushLanding: a tap on a phone notification lands where a tap in Alerts does
 * (webpush).
 *
 * Two roads in, one landing:
 *   - Karr was open: public/push-sw.js focuses it and posts `karr:push-open`.
 *   - Karr was closed: the worker opens the page with the landing in the URL
 *     (`?karrpush=`). `capturePushLanding()` runs in main.tsx before the router
 *     sees the URL, keeps the landing and takes the parameter out, so a reload
 *     or a shared link never replays it.
 *
 * Either way it waits for the sign-in to know the person's businesses, stands
 * in the one the alert is about (openAlert.standIn, the very same door), marks
 * the alert's rows read, and goes to the page through goToFix.
 *
 * @since webpush
 */
import { useEffect } from 'react';
import { useAuth } from '@/shared/hooks/useAuth';
import { goToFix } from '@/shared/errors/goToFix';
import { standIn } from '@/shared/phone/openAlert';
import { markMyNotificationsRead } from '@/shared/hooks/useNotifications';
import type { PushLanding } from '../../../supabase/functions/_shared/pushPayload';

export type { PushLanding };

let pending: PushLanding | null = null;

/** The landing a notification carried, or null when it is not one. */
export function parseLanding(v: unknown): PushLanding | null {
    if (!v || typeof v !== 'object') return null;
    const o = v as Record<string, unknown>;
    if (typeof o.href !== 'string' || !o.href.startsWith('/') || o.href.startsWith('//')) return null;
    const ids = Array.isArray(o.ids) ? o.ids.filter((x): x is string => typeof x === 'string').slice(0, 30) : [];
    return {
        href: o.href,
        ids,
        orgId: typeof o.orgId === 'string' ? o.orgId : null,
        orgName: typeof o.orgName === 'string' ? o.orgName : null,
    };
}

/** Out of a URL: the landing, and the URL without it. */
export function takeLandingFromUrl(href: string): { landing: PushLanding | null; clean: string } {
    let url: URL;
    try { url = new URL(href); } catch { return { landing: null, clean: href }; }
    const raw = url.searchParams.get('karrpush');
    if (raw === null) return { landing: null, clean: href };
    url.searchParams.delete('karrpush');
    let landing: PushLanding | null = null;
    try { landing = parseLanding(JSON.parse(raw)); } catch { landing = null; }
    return { landing, clean: `${url.pathname}${url.search}${url.hash}` };
}

/** main.tsx, before the router: keep a cold start's landing and clean the URL. */
export function capturePushLanding(): void {
    if (typeof window === 'undefined') return;
    const { landing, clean } = takeLandingFromUrl(window.location.href);
    if (!landing) return;
    pending = landing;
    try { window.history.replaceState(window.history.state, '', clean); } catch { /* keep the param */ }
}

/** Stand in, mark read, land. The same three steps as openAlert. */
export function landOn(l: PushLanding): void {
    if (l.ids.length) void markMyNotificationsRead(l.ids);
    if (l.orgId) standIn(l.orgId, l.orgName ?? '');
    void goToFix(l.href);
}

/**
 * Mounted once (AppLayout's NotificationEngine): runs a cold start's landing as
 * soon as the person's businesses are known, and every warm tap after that.
 */
export function usePushLanding(): void {
    const ready = useAuth((s) => !!s.profile && s.orgs.length > 0);

    useEffect(() => {
        if (!ready || !pending) return;
        const l = pending;
        pending = null;
        landOn(l);
    }, [ready]);

    useEffect(() => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
        const onMessage = (e: MessageEvent) => {
            const d = e.data as { type?: string; landing?: unknown } | null;
            if (d?.type !== 'karr:push-open') return;
            const l = parseLanding(d.landing);
            if (!l) return;
            if (useAuth.getState().profile) landOn(l);
            else pending = l;
        };
        navigator.serviceWorker.addEventListener('message', onMessage);
        // Messages wait in a queue until the page says it is listening.
        try { navigator.serviceWorker.startMessages(); } catch { /* older browser: started by default */ }
        return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    }, []);
}
